import { downloadQrCard } from "../lib/qrCardGenerator";
import { useState, useEffect } from "react";
import { useAuth, API } from "../App";
import { Link } from "react-router-dom";
import axios from "axios";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Briefcase,
  Plus,
  Search,
  QrCode,
  Eye,
  Pencil,
  Trash2,
  Loader2,
  MapPin,
  AlertCircle,
  RefreshCw,
  Stethoscope,
  HeartPulse,
  FileText
} from "lucide-react";
import { toast } from "sonner";

const Offices = () => {
  const { user, token } = useAuth();
  const canManageBiomed = user?.role === "admin" || user?.is_biomed_officer === true;
  const [offices, setOffices] = useState([]);
  const [filteredOffices, setFilteredOffices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [floorFilter, setFloorFilter] = useState("all");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [qrDialogOpen, setQrDialogOpen] = useState(false);
  const [selectedOffice, setSelectedOffice] = useState(null);
  const [regeneratingQr, setRegeneratingQr] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    floor: "",
    building: "المبنى الرئيسي",
    notes: ""
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchOffices();
  }, [token]);

  useEffect(() => {
    filterOffices();
  }, [offices, searchQuery, floorFilter]);

  const fetchOffices = async () => {
    try {
      const response = await axios.get(`${API}/offices`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOffices(response.data);
    } catch (error) {
      toast.error("فشل في تحميل المكاتب");
    } finally {
      setLoading(false);
    }
  };

  const filterOffices = () => {
    let filtered = [...offices];
    
    if (searchQuery) {
      filtered = filtered.filter(office =>
        office.name.includes(searchQuery) ||
        office.floor.includes(searchQuery) ||
        office.building.includes(searchQuery)
      );
    }
    
    if (floorFilter !== "all") {
      filtered = filtered.filter(office => office.floor === floorFilter);
    }
    
    setFilteredOffices(filtered);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.floor) {
      toast.error("يرجى ملء جميع الحقول المطلوبة");
      return;
    }

    setSubmitting(true);
    try {
      if (selectedOffice) {
        await axios.put(`${API}/offices/${selectedOffice.id}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("تم تحديث المكتب بنجاح");
      } else {
        await axios.post(`${API}/offices`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("تم إضافة المكتب بنجاح");
      }
      fetchOffices();
      setDialogOpen(false);
      resetForm();
    } catch (error) {
      toast.error(error.response?.data?.detail || "حدث خطأ");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (officeId) => {
    if (!window.confirm("هل أنت متأكد من حذف هذا المكتب؟")) return;
    
    try {
      await axios.delete(`${API}/offices/${officeId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("تم حذف المكتب بنجاح");
      fetchOffices();
    } catch (error) {
      toast.error("فشل في حذف المكتب");
    }
  };

  const resetForm = () => {
    setFormData({ name: "", floor: "", building: "المبنى الرئيسي", notes: "" });
    setSelectedOffice(null);
  };

  const openEditDialog = (office) => {
    setSelectedOffice(office);
    setFormData({
      name: office.name,
      floor: office.floor,
      building: office.building,
      notes: office.notes
    });
    setDialogOpen(true);
  };

  const openQRDialog = (office) => {
    setSelectedOffice(office);
    setQrDialogOpen(true);
  };

  const handleRegenerateSingleQr = async () => {
    if (!selectedOffice) return;
    setRegeneratingQr(true);
    try {
      const res = await axios.post(
        `${API}/location/office/${selectedOffice.id}/regenerate-qr?base_url=${encodeURIComponent(window.location.origin)}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSelectedOffice({ ...selectedOffice, qr_code: res.data.qr_code });
      toast.success("تم إعادة توليد رمز QR بنجاح");
      fetchOffices();
    } catch {
      toast.error("فشل إعادة توليد رمز QR");
    } finally {
      setRegeneratingQr(false);
    }
  };

  const toggleTreatmentPlan = async (office) => {
    try {
      const res = await axios.patch(
        `${API}/offices/${office.id}/treatment-plan-toggle`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(res.data.show_treatment_plan ? "تم تفعيل خيار الخطة العلاجية" : "تم إلغاء خيار الخطة العلاجية");
      fetchOffices();
    } catch {
      toast.error("فشل في تحديث الخيار");
    }
  };

  const toggleMedicalReport = async (office) => {
    try {
      const res = await axios.patch(
        `${API}/office/${office.id}/medical-report-toggle`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(res.data.show_medical_report ? "تم تفعيل خيار التقرير الطبي على QR" : "تم إلغاء خيار التقرير الطبي");
      fetchOffices();
    } catch {
      toast.error("فشل في تحديث الخيار");
    }
  };

  const toggleBiomed = async (office) => {
    try {
      const res = await axios.patch(
        `${API}/offices/${office.id}/biomed-toggle`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(
        res.data.has_biomed_devices
          ? "تم تفعيل إمكانية إضافة أجهزة طبية لهذا المكتب"
          : "تم إلغاء إمكانية إضافة أجهزة طبية لهذا المكتب"
      );
      fetchOffices();
    } catch {
      toast.error("فشل في تحديث الخيار");
    }
  };

  const floors = [...new Set(offices.map(o => o.floor))].sort();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="lucent-loader"><img src="/lucent-logo.png" alt="LUCENT" /><div className="lucent-loader-bar"></div></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="offices-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-[#655dc6]">المكاتب</h1>
          <p className="text-slate-500 mt-1">إدارة مكاتب المركز ({offices.length} مكتب)</p>
        </div>
        {user?.role === "admin" && (
          <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button className="bg-[#655dc6] hover:bg-[#5449a8] text-white" data-testid="add-office-btn">
                <Plus className="w-4 h-4 ml-2" />
                إضافة مكتب
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md" dir="rtl">
              <DialogHeader>
                <DialogTitle className="text-[#655dc6]">
                  {selectedOffice ? "تعديل المكتب" : "إضافة مكتب جديد"}
                </DialogTitle>
                <DialogDescription>
                  أدخل بيانات المكتب
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label>اسم المكتب *</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="مثال: مكتب الإدارة"
                    className="text-right"
                    data-testid="office-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>الطابق *</Label>
                  <Input
                    value={formData.floor}
                    onChange={(e) => setFormData({ ...formData, floor: e.target.value })}
                    placeholder="مثال: الطابق 1"
                    className="text-right"
                    data-testid="office-floor-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>المبنى</Label>
                  <Input
                    value={formData.building}
                    onChange={(e) => setFormData({ ...formData, building: e.target.value })}
                    placeholder="مثال: المبنى الرئيسي"
                    className="text-right"
                    data-testid="office-building-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>ملاحظات</Label>
                  <Input
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    placeholder="ملاحظات إضافية"
                    className="text-right"
                    data-testid="office-notes-input"
                  />
                </div>
                <div className="flex gap-3 pt-4">
                  <Button
                    type="submit"
                    className="flex-1 bg-[#655dc6] hover:bg-[#5449a8] text-white"
                    disabled={submitting}
                    data-testid="submit-office-btn"
                  >
                    {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : selectedOffice ? "تحديث" : "إضافة"}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => { setDialogOpen(false); resetForm(); }}
                    className="flex-1"
                  >
                    إلغاء
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Filters */}
      <Card className="border-0 shadow-sm">
        <CardContent className="pt-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <Input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="البحث في المكاتب..."
                className="pr-10 text-right"
                data-testid="search-offices-input"
              />
            </div>
            <Select value={floorFilter} onValueChange={setFloorFilter}>
              <SelectTrigger className="w-full sm:w-48" data-testid="floor-filter">
                <SelectValue placeholder="جميع الطوابق" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">جميع الطوابق</SelectItem>
                {floors.map(floor => (
                  <SelectItem key={floor} value={floor}>{floor}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Offices Grid */}
      {filteredOffices.length > 0 ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {filteredOffices.map((office) => (
            <Card
              key={office.id}
              className="card-hover border-0 shadow-sm group"
              data-testid={`office-card-${office.id}`}
            >
              <CardContent className="pt-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="w-12 h-12 rounded-xl bg-[#F5F3F9] flex items-center justify-center group-hover:bg-[#655dc6] transition-colors">
                    <Briefcase className="w-6 h-6 text-[#655dc6] group-hover:text-white transition-colors" />
                  </div>
                  <button
                    onClick={() => openQRDialog(office)}
                    className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
                    title="عرض رمز QR"
                    data-testid={`qr-btn-${office.id}`}
                  >
                    <QrCode className="w-5 h-5 text-[#9b8fd4]" />
                  </button>
                </div>
                
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-bold text-lg text-[#655dc6]">{office.name}</h3>
                  {office.open_requests_count > 0 && (
                    <Badge 
                      className="bg-amber-100 text-amber-700 hover:bg-amber-100 flex items-center gap-1"
                      data-testid={`open-requests-badge-${office.id}`}
                    >
                      <AlertCircle className="w-3 h-3" />
                      {office.open_requests_count}
                    </Badge>
                  )}
                </div>
                
                <div className="flex items-center gap-2 text-sm text-slate-500 mb-4">
                  <MapPin className="w-4 h-4" />
                  <span>{office.floor}</span>
                  {office.building && <span>• {office.building}</span>}
                </div>

                <div className="flex gap-2">
                  <Link to={`/offices/${office.id}`} className="flex-1">
                    <Button variant="outline" className="w-full" size="sm" data-testid={`view-office-${office.id}`}>
                      <Eye className="w-4 h-4 ml-1" />
                      عرض
                    </Button>
                  </Link>
                  {user?.role === "admin" && (
                    <>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => toggleTreatmentPlan(office)}
                        title={office.show_treatment_plan ? "إلغاء خيار الخطة العلاجية" : "تفعيل خيار الخطة العلاجية على QR"}
                        className={office.show_treatment_plan ? "bg-[#655dc6] text-white hover:bg-[#5449a8]" : ""}
                        data-testid={`treatment-plan-toggle-${office.id}`}
                      >
                        <Stethoscope className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => toggleMedicalReport(office)}
                        title={office.show_medical_report ? "إلغاء خيار التقرير الطبي" : "تفعيل خيار التقرير الطبي على QR"}
                        className={office.show_medical_report ? "bg-sky-600 text-white hover:bg-sky-700 border-sky-600" : ""}
                        data-testid={`medical-report-toggle-${office.id}`}
                      >
                        <FileText className="w-4 h-4" />
                      </Button>
                      {canManageBiomed && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => toggleBiomed(office)}
                          title={office.has_biomed_devices ? "إلغاء إمكانية إضافة أجهزة طبية" : "تفعيل إمكانية إضافة أجهزة طبية"}
                          className={office.has_biomed_devices ? "bg-emerald-600 text-white hover:bg-emerald-700 border-emerald-600" : ""}
                          data-testid={`biomed-toggle-${office.id}`}
                        >
                          <HeartPulse className="w-4 h-4" />
                        </Button>
                      )}
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => openEditDialog(office)}
                        data-testid={`edit-office-${office.id}`}
                      >
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className="text-red-500 hover:bg-red-50"
                        onClick={() => handleDelete(office.id)}
                        data-testid={`delete-office-${office.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card className="border-0 shadow-sm">
          <CardContent className="py-16 text-center">
            <Briefcase className="w-16 h-16 mx-auto mb-4 text-slate-300" />
            <h3 className="text-lg font-semibold text-[#655dc6] mb-2">لا توجد مكاتب</h3>
            <p className="text-slate-500">
              {searchQuery || floorFilter !== "all" ? "لم يتم العثور على نتائج مطابقة" : "لم يتم إضافة مكاتب بعد"}
            </p>
          </CardContent>
        </Card>
      )}

      {/* QR Code Dialog */}
      <Dialog open={qrDialogOpen} onOpenChange={setQrDialogOpen}>
        <DialogContent className="sm:max-w-md" dir="rtl">
          <DialogHeader>
            <DialogTitle className="text-[#655dc6]">رمز QR - {selectedOffice?.name}</DialogTitle>
            <DialogDescription>
              امسح هذا الرمز للإبلاغ عن مشكلة صيانة
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col items-center py-6">
            {selectedOffice?.qr_code && (
              <div className="qr-code-container">
                <img
                  src={`data:image/png;base64,${selectedOffice.qr_code}`}
                  alt={`QR Code for ${selectedOffice.name}`}
                  className="w-48 h-48"
                />
              </div>
            )}
            <p className="mt-4 text-center text-slate-500 text-sm">
              {selectedOffice?.floor} • {selectedOffice?.building}
            </p>
          </div>
          <div className="flex gap-2">
            <Button
              onClick={async () => {
                await downloadQrCard({
                  qrBase64: selectedOffice.qr_code,
                  name: selectedOffice.name,
                  floor: selectedOffice.floor,
                  building: selectedOffice.building
                });
              }}
              className="flex-1 bg-[#655dc6] hover:bg-[#5449a8] text-white"
            >
              تحميل رمز QR
            </Button>
            <Button
              variant="outline"
              onClick={handleRegenerateSingleQr}
              disabled={regeneratingQr}
              className="flex-1 gap-1"
            >
              {regeneratingQr ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
              إعادة توليد
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Offices;
